import PageLayout from '@hellodarwin/app-core/lib/layouts/page-layout';
import Button from '@hellodarwin/core/lib/components/common/button';
import Modal from '@hellodarwin/core/lib/components/common/hd-modal';
import Pagination from '@hellodarwin/core/lib/components/common/pagination';
import Select from '@hellodarwin/core/lib/components/common/select';
import Spin from '@hellodarwin/core/lib/components/common/spin';
import Typography from '@hellodarwin/core/lib/components/common/typography';
import useLocale from '@hellodarwin/core/lib/features/providers/locale-provider';
import {
  usePagination,
  useParams,
} from '@hellodarwin/core/lib/features/providers/pagination-provider';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import Settings from '@hellodarwin/icons/dist/icons/Settings';
import Empty from 'antd/es/empty';
import { usePremiumAccess } from '@hellodarwin/app-core/lib/providers/premium-access-provider';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../app/app-hooks';
import {
  grantAlertKinds,
  isGrantAlertKind,
  type GrantAlertHistoryItem,
  type GrantAlertKind,
} from '../../features/api/grant-alert-history-types';
import {
  fetchGrantAlertHistory,
  selectGrantAlertHistoryItems,
  selectGrantAlertHistoryStatus,
  selectGrantAlertHistoryTotal,
} from '../../features/api/slices/grant-alert-history-slice';
import {
  fetchGrantProfiles,
  selectAllGrantProfiles,
} from '../../features/api/slices/grant-profiles-slice';
import { useNewClientApi } from '../../features/api/use-client-api';
import {
  GrantNotificationAudienceChip,
  GrantNotificationAudienceList,
} from '@hellodarwin/core/lib/components/notifications/card/styles';
import GrantAlertSettings from './grant-alert-settings';
import {
  AlertActions,
  AlertBody,
  AlertContent,
  AlertDate,
  AlertFilterControl,
  AlertFilters,
  AlertList,
  AlertPagination,
  AlertRow,
  AlertTitle,
  AlertsContainer,
  AlertsDescription,
  AlertsHeader,
  AlertsHeaderCopy,
  AlertsLoading,
  AlertsSettingsContent,
} from './styles';

const alertKindTranslationKey: Record<GrantAlertKind, string> = {
  created: 'alerts|kindCreated',
  opening_soon: 'alerts|kindOpeningSoon',
  opened: 'alerts|kindOpened',
  closing_soon: 'alerts|kindClosingSoon',
  closed: 'alerts|kindClosed',
  updated: 'alerts|kindUpdated',
};

const ClientNotificationsPage = () => {
  const api = useNewClientApi();
  const dispatch = useAppDispatch();
  const { selectedLocale } = useLocale();
  const { t } = useTranslations();
  const alerts = useAppSelector(selectGrantAlertHistoryItems);
  const total = useAppSelector(selectGrantAlertHistoryTotal);
  const status = useAppSelector(selectGrantAlertHistoryStatus);
  const { activePage, pageSize, goToPage } = usePagination();
  const { addParam, getArrayParam, removeParam } = useParams();
  const { accessLevel } = usePremiumAccess();
  const isExpertAccess = accessLevel === 'expert';
  const grantProfiles = useAppSelector(selectAllGrantProfiles);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const selectedTypes = useMemo(
    () => getArrayParam('types').filter(isGrantAlertKind),
    [getArrayParam],
  );
  const selectedClients = useMemo(
    () => getArrayParam('clients'),
    [getArrayParam],
  );

  useEffect(() => {
    if (!isExpertAccess) return;
    void dispatch(fetchGrantProfiles({ api }));
  }, [api, dispatch, isExpertAccess]);

  const loadAlerts = useCallback(() => {
    return dispatch(
      fetchGrantAlertHistory({
        api,
        locale: selectedLocale,
        page: activePage,
        limit: pageSize,
        types: selectedTypes,
        grantProfileIds: selectedClients,
      }),
    );
  }, [
    activePage,
    api,
    dispatch,
    pageSize,
    selectedClients,
    selectedLocale,
    selectedTypes,
  ]);

  useEffect(() => {
    const request = loadAlerts();
    return () => request.abort();
  }, [loadAlerts]);

  const loadingInitial = status === 'pending';
  const failedWithoutAlerts = status === 'failed' && alerts.length === 0;
  const filterOptions = useMemo(
    () =>
      grantAlertKinds.map((type) => ({
        value: type,
        label: t(alertKindTranslationKey[type]),
      })),
    [t],
  );
  const deliveredAtFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat(selectedLocale, {
        dateStyle: 'medium',
        timeStyle: 'short',
      }),
    [selectedLocale],
  );

  const formatDeliveredAt = (alert: GrantAlertHistoryItem) =>
    deliveredAtFormatter.format(new Date(alert.delivered_at));

  const clientFilterOptions = useMemo(
    () =>
      grantProfiles.map((profile) => ({
        value: profile.grant_profile_id,
        label: profile.name,
      })),
    [grantProfiles],
  );

  const handleFilterChange = (key: string, values: string[]) => {
    goToPage(1);
    if (values.length > 0) {
      addParam(key, values.join(','));
      return;
    }
    removeParam(key);
  };

  return (
    <>
      <PageLayout
        app="client"
        tabTitle={t('alerts|title')}
        breadcrumbs={[
          { breadcrumbName: t('navigation|home'), path: '/' },
          { breadcrumbName: t('navigation|alerts'), path: '/alerts' },
        ]}
      >
        <AlertsContainer>
          <AlertsHeader>
            <AlertsHeaderCopy>
              <Typography elementTheme="h5">{t('alerts|title')}</Typography>
              <AlertsDescription>
                <Typography elementTheme="body1">
                  {t('alerts|description')}
                </Typography>
                {!loadingInitial && alerts.length > 0 && (
                  <Typography elementTheme="body2">
                    {t('alerts|deliveredCount', { count: total })}
                  </Typography>
                )}
              </AlertsDescription>
            </AlertsHeaderCopy>
            <AlertActions>
              <Button
                variant="outline"
                headingIcon={<Settings size={18} />}
                onClick={() => setSettingsOpen(true)}
              >
                {t('alerts|settingsButton')}
              </Button>
            </AlertActions>
          </AlertsHeader>

          <AlertFilters>
            <AlertFilterControl>
              <Typography elementTheme="body2">
                {t('alerts|filterByType')}
              </Typography>
              <Select
                allowClear
                aria-label={t('alerts|filterByType')}
                maxTagCount="responsive"
                mode="multiple"
                onChange={(types) => handleFilterChange('types', types)}
                options={filterOptions}
                placeholder={t('alerts|allAlertTypes')}
                value={selectedTypes}
              />
            </AlertFilterControl>
            {isExpertAccess && clientFilterOptions.length > 0 && (
              <AlertFilterControl>
                <Typography elementTheme="body2">
                  {t('alerts|filterByClient')}
                </Typography>
                <Select
                  allowClear
                  aria-label={t('alerts|filterByClient')}
                  maxTagCount="responsive"
                  mode="multiple"
                  onChange={(clients) => handleFilterChange('clients', clients)}
                  options={clientFilterOptions}
                  placeholder={t('alerts|allClients')}
                  value={selectedClients}
                />
              </AlertFilterControl>
            )}
          </AlertFilters>

          {loadingInitial ? (
            <AlertsLoading>
              <Spin />
            </AlertsLoading>
          ) : failedWithoutAlerts ? (
            <AlertsLoading>
              <Empty description={t('alerts|loadError')} />
              <Button onClick={loadAlerts}>{t('alerts|retry')}</Button>
            </AlertsLoading>
          ) : alerts.length === 0 ? (
            <AlertsLoading>
              <Empty
                description={t(
                  selectedTypes.length > 0 || selectedClients.length > 0
                    ? 'alerts|emptyFiltered'
                    : 'alerts|empty',
                )}
              />
            </AlertsLoading>
          ) : (
            <>
              <AlertList>
                {alerts.map((alert) => (
                  <AlertRow key={alert.alert_id}>
                    <AlertContent>
                      <AlertTitle>{alert.grant_title}</AlertTitle>
                      <AlertBody>
                        {t(alertKindTranslationKey[alert.kind])}
                      </AlertBody>
                      {!!alert.grant_profile_names?.length && (
                        <GrantNotificationAudienceList
                          aria-label={t('alerts|aboutClients')}
                        >
                          {/* Position-keyed: two Grant Profiles can share a name. */}
                          {alert.grant_profile_names.map((name, index) => (
                            <GrantNotificationAudienceChip
                              key={`${alert.alert_id}-${index}`}
                            >
                              {name}
                            </GrantNotificationAudienceChip>
                          ))}
                        </GrantNotificationAudienceList>
                      )}
                    </AlertContent>
                    <AlertDate dateTime={alert.delivered_at}>
                      {formatDeliveredAt(alert)}
                    </AlertDate>
                  </AlertRow>
                ))}
              </AlertList>
              {total > pageSize && (
                <AlertPagination>
                  <Pagination
                    align="end"
                    currentPage={activePage}
                    onPageChange={goToPage}
                    pageSize={pageSize}
                    total={total}
                  />
                </AlertPagination>
              )}
            </>
          )}
        </AlertsContainer>
      </PageLayout>

      {settingsOpen && (
        <Modal
          open={settingsOpen}
          size="extra-large"
          handleCancel={() => setSettingsOpen(false)}
        >
          <AlertsSettingsContent>
            <Typography elementTheme="h5">
              {t('alerts|settingsTitle')}
            </Typography>
            <GrantAlertSettings />
          </AlertsSettingsContent>
        </Modal>
      )}
    </>
  );
};

export default ClientNotificationsPage;
